export type PlanName = 'starter' | 'pro' | 'ultra';

export interface PlanLimits {
  projects: number;
  messages: number;
  artifacts: number | null; // null = unlimited
  figmaExports: number | null; // null = unlimited
  allTemplates: boolean;
  sourcesPerProject: number | null; // null = unlimited
  maxFileSize: number; // in bytes
  ghostTextPerMonth: number | null; // null = unlimited
}

export interface UsageData {
  messages: {
    current: number;
    limit: number;
    lifetime: number;
  };
  artifacts: {
    current: number;
    limit: number | null;
    lifetime: number;
  };
  figmaExports: {
    current: number;
    limit: number | null;
    lifetime: number;
  };
  projects: {
    current: number;
    limit: number;
  };
}

export interface UsageStats {
  plan: PlanName;
  billingPeriod: {
    start: Date | null;
    end: Date | null;
  };
  usage: UsageData;
  features: {
    allTemplates: boolean;
  };
}

/**
 * Admin-configurable limit overrides for individual users.
 * When a field is present, it overrides the plan's default limit.
 * When absent/undefined, the plan limit applies.
 * A value of `null` means unlimited.
 */
export interface LimitOverrides {
  projects?: number | null;           // null = unlimited
  messages?: number | null;           // null = unlimited
  artifacts?: number | null;          // null = unlimited
  figmaExports?: number | null;       // null = unlimited
  sourcesPerProject?: number | null;  // null = unlimited
  ghostTextPerMonth?: number | null;  // null = unlimited
}

// Plan limits configuration (mirrors backend)
export const PLAN_LIMITS: Record<PlanName, PlanLimits> = {
  starter: {
    projects: 1,
    messages: 100,
    artifacts: 5,
    figmaExports: 2,
    allTemplates: false,
    sourcesPerProject: 5,
    maxFileSize: 5242880, // 5MB
    ghostTextPerMonth: 5,
  },
  pro: {
    projects: 10,
    messages: 500,
    artifacts: 50,
    figmaExports: 25,
    allTemplates: true,
    sourcesPerProject: 15,
    maxFileSize: 10485760, // 10MB
    ghostTextPerMonth: null, // Unlimited
  },
  ultra: {
    projects: 25,
    messages: 1000,
    artifacts: null, // Unlimited
    figmaExports: null, // Unlimited
    allTemplates: true,
    sourcesPerProject: 20,
    maxFileSize: 10485760, // 10MB
    ghostTextPerMonth: null, // Unlimited
  },
};

// Templates available for Starter plan
export const STARTER_TEMPLATES = [
  'Wireframes',
  'Accessibility Compliance',
  'Sitemaps / IA',
  'Summarize Transcripts',
  'Competitive Analysis',
];

/**
 * Check if a template is available for a given plan
 */
export function isTemplateAvailable(templateTitle: string, planName: PlanName): boolean {
  if (PLAN_LIMITS[planName].allTemplates) {
    return true;
  }
  return STARTER_TEMPLATES.includes(templateTitle);
}
